import React, { useLayoutEffect, useRef, useState } from 'react';
import { accentOrPrimary } from '../../../puck/fields/createPuckColorField';
import { hotspotNumberPlacement } from './hotspotNumberPlacement';
import { hotspotMarkerStyle, hotspotNumberSide } from './hotspotPoints';
import classes from './HotspotImageBlock.module.css';

export const hotspotMarkerVariables = ({ accent = '', markerSize = '2.75rem' }) => ({
  '--hotspot-accent': accentOrPrimary(accent),
  '--hotspot-size': markerSize || '2.75rem',
});

export function HotspotNumber({ point, index, preview = false }) {
  const ref = useRef(null);
  const [placement, setPlacement] = useState(null);
  const preferredSide = hotspotNumberSide(point);
  useLayoutEffect(() => {
    const number = ref.current;
    const surface = number?.closest('[data-hotspot-surface]');
    const frame = number?.parentElement;
    if (!surface || !frame) return undefined;
    const update = () => {
      const imageBounds = surface.getBoundingClientRect();
      const frameBounds = frame.getBoundingClientRect();
      const numberBounds = number.getBoundingClientRect();
      if (!imageBounds.width || !imageBounds.height || !numberBounds.width || !numberBounds.height) return;
      const ownerWindow = number.ownerDocument.defaultView;
      const frameStyle = ownerWindow.getComputedStyle(frame);
      const scaleX = frameBounds.width / (parseFloat(frameStyle.width) || frame.offsetWidth || frameBounds.width) || 1;
      const scaleY = frameBounds.height / (parseFloat(frameStyle.height) || frame.offsetHeight || frameBounds.height) || 1;
      const previewScale = parseFloat(ownerWindow.getComputedStyle(number).getPropertyValue('--hotspot-preview-scale')) || 1;
      const next = hotspotNumberPlacement({
        image: imageBounds,
        frame: { x: frameBounds.left - imageBounds.left, y: frameBounds.top - imageBounds.top, width: frameBounds.width, height: frameBounds.height },
        number: numberBounds, preferredSide, gap: 8 * previewScale * scaleX,
      });
      const value = {
        side: next.side,
        left: (next.x + imageBounds.left - frameBounds.left) / scaleX - (parseFloat(frameStyle.borderLeftWidth) || 0),
        top: (next.y + imageBounds.top - frameBounds.top) / scaleY - (parseFloat(frameStyle.borderTopWidth) || 0),
        maximumSize: Math.min(imageBounds.width / scaleX, imageBounds.height / scaleY),
      };
      setPlacement((current) => current && Object.keys(value).every((key) => current[key] === value[key]) ? current : value);
    };
    update();
    const ownerWindow = number.ownerDocument.defaultView;
    const Observer = ownerWindow.ResizeObserver;
    const observer = Observer ? new Observer(update) : null;
    [surface, frame, number].forEach((element) => observer?.observe(element));
    surface.addEventListener('load', update, true);
    ownerWindow.addEventListener('resize', update);
    return () => {
      observer?.disconnect();
      surface.removeEventListener('load', update, true);
      ownerWindow.removeEventListener('resize', update);
    };
  }, [point.shape, point.x, point.y, point.width, point.height, preferredSide]);
  if (point.shape !== 'rectangle') return index + 1;
  return (
    <span ref={ref} data-hotspot-number aria-hidden="true" className={`${preview ? classes.positionMarker : classes.marker} ${classes.rectangleNumber}`} data-side={placement?.side || preferredSide}
      style={placement ? { left: placement.left, top: placement.top, right: 'auto', bottom: 'auto', transform: 'translate(-50%, -50%)', maxWidth: placement.maximumSize, maxHeight: placement.maximumSize } : undefined}>
      {index + 1}
    </span>
  );
}

export function HotspotPositionMarker({ point, index, selected = true }) {
  return (
    <span aria-hidden="true" className={`${classes.positionMarker} ${point.shape === 'rectangle' ? classes.rectangle : ''}`} style={{ ...hotspotMarkerStyle(point), opacity: selected ? 1 : 0.45 }}>
      <HotspotNumber point={point} index={index} preview />
    </span>
  );
}
